import { z } from "zod";
import { adminOperationsExtendedSchemaVersionSchema, adminReasonCodeSchema, adminSafeIdentifierSchema, adminSha256Schema, adminUlidSchema, } from "./admin-operations-core.js";
import { adminSubjectTypeSchema } from "./admin-operations-cases.js";
export const adminActionKindSchema = z.enum([
    "DIRECT_GIFT_DELIVERY_RETRY",
    "VOUCHER_SUSPEND",
    "VOUCHER_REVOKE",
    "VOUCHER_REPLACE_LOST",
    "ORDER_PROJECTION_RECONCILE",
    "WORKFLOW_RETRY",
    "STORY_ASSISTANCE_RECOVERY",
    "QUARANTINED_MESSAGE_REPROCESS",
    "LC92_FULFILLMENT_COMMAND",
    // Erasing a customer's account at their request (owner decision, 2026-10-19).
    "ACCOUNT_ERASE",
]);
/** Retires one failed legacy request; this is not a retry or a provider invoice. */
export const adminStoryAssistanceRecoverySchema = z
    .object({
    version: z.literal("ADMIN_STORY_ASSISTANCE_RECOVERY_V1"),
    bookId: adminUlidSchema,
    jobId: adminUlidSchema,
    sourceRevision: z.number().int().nonnegative(),
    projectRevision: z.number().int().nonnegative(),
    sourceDigestSha256: adminSha256Schema,
    authorityDigestSha256: adminSha256Schema,
    executionArn: z.string().min(1).max(512),
    executionStatus: z.enum(["SUCCEEDED", "FAILED", "TIMED_OUT", "ABORTED"]),
    executionStoppedAt: z.string().datetime(),
    costState: z.literal("UNKNOWN"),
    accountingHoldMicros: z.literal(750_000),
    accountingPolicy: z.literal("LEGACY_INTENT_THREE_ATTEMPT_HOLD_2026-10-02.1"),
    ownerCeilingMicros: z.union([z.literal(10_000_000), z.literal(30_000_000)]),
    historyPreserved: z.literal(true),
    providerDispatchAuthorized: z.literal(false),
    completeOwnerCeilingEvidence: z.literal(false),
})
    .strict();
/**
 * Retires one stopped picture request still holding its book. An unknown call is counted at its
 * largest possible cost; a request that made no call is released at $0; known usage is settled
 * as reported. It never retries, refunds or confirms an invoice.
 */
export const adminPublicationJobRecoverySchema = z
    .object({
    version: z.literal("ADMIN_PUBLICATION_JOB_RECOVERY_V1"),
    bookId: adminUlidSchema,
    jobId: adminUlidSchema,
    task: z.enum([
        "REPRESENTATIVE_LAYOUT_PROOFS",
        "TARGETED_PUBLICATION_PROOF_REPLACEMENT",
        "NATIVE_PUBLICATION_MEDIA",
        "NATIVE_PUBLICATION_COVER",
    ]),
    failureReason: z.string().max(120),
    sourceRevision: z.number().int().nonnegative(),
    projectRevision: z.number().int().nonnegative(),
    authorityDigestSha256: adminSha256Schema,
    startedCalls: z.number().int().nonnegative().max(20),
    reportedCalls: z.number().int().nonnegative().max(20),
    unknownCalls: z.number().int().nonnegative().max(20),
    knownReportedCostMicros: z.number().int().nonnegative(),
    countedCostMicros: z.number().int().nonnegative(),
    quotedCostMicros: z.number().int().positive(),
    costState: z.enum(["UNKNOWN", "KNOWN", "NONE"]),
    replacementFenceLifted: z.boolean(),
    accountingPolicy: z.literal("UNKNOWN_CALL_COUNTED_AT_MAXIMUM_2026-10-13.1"),
    historyPreserved: z.literal(true),
    providerDispatchAuthorized: z.literal(false),
})
    .strict();
/** The evidence a stuck-request recovery reviews: a legacy story-help request or a picture job. */
export const adminStuckRequestRecoverySchema = z.union([
    adminStoryAssistanceRecoverySchema,
    adminPublicationJobRecoverySchema,
]);
/**
 * What erasing an account removes and what it keeps, counted when it is reviewed (2026-10-21).
 * Books the customer owns are purged whole; their places in other people's books are removed;
 * support requests keep their reference but lose their words; purchases are kept.
 */
export const adminAccountErasureReviewSchema = z
    .object({
    version: z.literal("ADMIN_ACCOUNT_ERASURE_REVIEW_V1"),
    customerId: adminUlidSchema,
    supportTicketId: adminUlidSchema,
    // The request itself, so the approving owner sees what was asked and when (review).
    requestReceivedAt: z.string().datetime(),
    requestState: z.string().regex(/^[A-Z_]{2,40}$/u),
    ownedBooks: z.number().int().nonnegative(),
    sharedBooks: z.number().int().nonnegative(),
    photos: z.number().int().nonnegative(),
    supportRequests: z.number().int().nonnegative(),
    purchasesKept: z.number().int().nonnegative(),
    countsComplete: z.boolean(),
})
    .strict();
export const adminAccountErasureStateSchema = z.enum([
    "SCHEDULED",
    "BOOKS_PURGING",
    "ACCOUNT_DELETING",
    "JOURNEY_PENDING",
    "COMPLETED",
]);
export const adminAccountErasureStatusCommandSchema = z
    .object({ caseId: adminUlidSchema, customerId: adminUlidSchema })
    .strict();
export const adminAccountErasureStatusResponseSchema = z
    .object({
    data: z
        .object({
        erasure: z
            .object({
            customerId: adminUlidSchema,
            state: adminAccountErasureStateSchema,
            requestedAt: z.string().datetime(),
            updatedAt: z.string().datetime(),
            completedAt: z.string().datetime().optional(),
            failureCode: adminReasonCodeSchema.optional(),
            removed: z.record(z.string().max(40), z.number().int().nonnegative()).optional(),
        })
            .strict()
            .nullable(),
    })
        .strict(),
})
    .strict();
export const adminActionPreviewCommandSchema = z
    .object({
    caseId: adminUlidSchema,
    action: adminActionKindSchema,
    subjectType: adminSubjectTypeSchema,
    subjectId: adminSafeIdentifierSchema,
    reasonCode: adminReasonCodeSchema,
})
    .strict();
export const adminActionPreviewSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    id: adminUlidSchema,
    caseId: adminUlidSchema,
    action: adminActionKindSchema,
    subjectType: adminSubjectTypeSchema,
    subjectId: adminSafeIdentifierSchema,
    expectedVersion: z.number().int().nonnegative(),
    currentState: adminSafeIdentifierSchema,
    proposedState: adminSafeIdentifierSchema,
    affectedRecordCount: z.number().int().positive().max(100),
    impact: z.enum(["BOUNDED", "HIGH_IMPACT"]),
    previewDigest: adminSha256Schema,
    confirmationPhrase: z.string().min(6).max(80),
    createdBy: z.string().min(1).max(128),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    storyAssistanceRecovery: adminStuckRequestRecoverySchema.optional(),
    accountErasure: adminAccountErasureReviewSchema.optional(),
})
    .strict()
    .superRefine((preview, context) => {
    const erasure = preview.accountErasure;
    if (preview.action === "ACCOUNT_ERASE") {
        if (!erasure ||
            preview.subjectType !== "CUSTOMER" ||
            erasure.customerId !== preview.subjectId)
            context.addIssue({
                code: "custom",
                path: ["accountErasure"],
                message: "Erasure requires the reviewed account's own counts.",
            });
    }
    else if (erasure) {
        context.addIssue({
            code: "custom",
            path: ["accountErasure"],
            message: "Erasure counts may not authorize a different action.",
        });
    }
    const recovery = preview.storyAssistanceRecovery;
    if (preview.action === "STORY_ASSISTANCE_RECOVERY") {
        if (!recovery || preview.subjectType !== "BOOK" || recovery.bookId !== preview.subjectId)
            context.addIssue({
                code: "custom",
                path: ["storyAssistanceRecovery"],
                message: "Recovery requires exact book-bound accounting evidence.",
            });
    }
    else if (recovery) {
        context.addIssue({
            code: "custom",
            path: ["storyAssistanceRecovery"],
            message: "Recovery evidence may not authorize a different action.",
        });
    }
});
export const adminConfirmActionCommandSchema = z
    .object({
    previewId: adminUlidSchema,
    previewDigest: adminSha256Schema,
    confirmationPhrase: z.string().min(6).max(80),
    reasonCode: adminReasonCodeSchema,
    idempotencyKey: z.string().trim().min(8).max(128),
})
    .strict();
export const adminApprovalSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    id: adminUlidSchema,
    previewId: adminUlidSchema,
    requestedBy: z.string().min(1).max(128),
    approvedBy: z.string().min(1).max(128),
    state: z.literal("APPROVED"),
    approvedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
})
    .strict()
    .refine((approval) => approval.requestedBy !== approval.approvedBy, {
    path: ["approvedBy"],
    message: "A second operator is required.",
});
export const adminApproveActionCommandSchema = z
    .object({
    reasonCode: adminReasonCodeSchema,
})
    .strict();
export const adminCommandIntentSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    id: adminUlidSchema,
    previewId: adminUlidSchema,
    caseId: adminUlidSchema,
    action: adminActionKindSchema,
    subjectType: adminSubjectTypeSchema,
    subjectId: adminSafeIdentifierSchema,
    expectedVersion: z.number().int().nonnegative(),
    previewDigest: adminSha256Schema,
    reasonCode: adminReasonCodeSchema,
    requestedBy: z.string().min(1).max(128),
    approvedBy: z.string().min(1).max(128).optional(),
    state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "RECONCILIATION_REQUIRED"]),
    idempotencyKeyHash: adminSha256Schema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    resultCode: adminReasonCodeSchema.optional(),
})
    .strict();
export const adminCommandOutboxSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    commandId: adminUlidSchema,
    state: z.literal("PENDING_DISPATCH"),
    action: adminActionKindSchema,
    subjectType: adminSubjectTypeSchema,
    subjectId: adminSafeIdentifierSchema,
    expectedVersion: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
})
    .strict();
export const adminActionPreviewResponseSchema = z
    .object({ data: z.object({ preview: adminActionPreviewSchema }).strict() })
    .strict();
export const adminApprovalResponseSchema = z
    .object({ data: z.object({ approval: adminApprovalSchema }).strict() })
    .strict();
export const adminCommandResponseSchema = z
    .object({ data: z.object({ command: adminCommandIntentSchema }).strict() })
    .strict();
//# sourceMappingURL=admin-operations-actions.js.map